/**
 * Opportunity Distribution Center — admin entry point.
 *
 * Three views via tabs:
 *   • Opportunities — list + create
 *   • Templates     — email CMS
 *   • Detail        — drill-in: opportunity summary + recipients + draft editor
 *
 * Routes:
 *   /admin/odc                       → list / templates tabs
 *   /admin/odc/opportunities/:id     → detail (3-column workspace)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, Plus, Mail, Search, Inbox, FileText, Filter,
  Briefcase, Send, Archive,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import OdcTemplatesTab from './OdcTemplatesTab';
import OdcOpportunityFormModal from './OdcOpportunityFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new:       'bg-gray-100 text-gray-700 border-gray-200',
  matching:  'bg-amber-100 text-amber-800 border-amber-300 animate-pulse',
  ready:     'bg-blue-100 text-blue-800 border-blue-300',
  approved:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  sent:      'bg-purple-100 text-purple-800 border-purple-300',
  archived:  'bg-gray-200 text-gray-500 border-gray-300',
};

const OdcDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('opportunities');
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const r = await axios.get(`${API_URL}/api/odc/opportunities?${params.toString()}`, { headers });
      setOpps(r.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to load', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [search, statusFilter]);

  return (
    <div className="space-y-4" data-testid="odc-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Inbox className="w-6 h-6 text-[#00B4D8]" /> Opportunity Distribution Center
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Capture business opportunities, match them against the CRM, review every email and dispatch.
          </p>
        </div>
        {tab === 'opportunities' && (
          <Button onClick={() => setShowForm(true)} className="bg-[#0F172A] text-white" data-testid="odc-create-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Opportunity
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        <button
          onClick={() => setTab('opportunities')}
          data-testid="odc-tab-opportunities"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'opportunities' ? 'border-[#00B4D8] text-[#0F172A]' : 'border-transparent text-gray-500 hover:text-[#0F172A]'
          }`}
        >
          <Briefcase className="w-4 h-4 inline mr-1.5" /> Opportunities
        </button>
        <button
          onClick={() => setTab('templates')}
          data-testid="odc-tab-templates"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'templates' ? 'border-[#00B4D8] text-[#0F172A]' : 'border-transparent text-gray-500 hover:text-[#0F172A]'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-1.5" /> Email Templates
        </button>
      </div>

      {tab === 'opportunities' ? (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="pt-4 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search opportunities…"
                  className="w-full pl-9 pr-3 py-2 border rounded text-sm"
                  data-testid="odc-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded text-sm bg-white"
                data-testid="odc-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="new">New</option>
                <option value="ready">Ready (matched)</option>
                <option value="approved">Approved</option>
                <option value="sent">Sent</option>
                <option value="archived">Archived</option>
              </select>
              <Badge variant="outline" className="text-[11px]">
                <Filter className="w-3 h-3 mr-1" /> {opps.length} result{opps.length === 1 ? '' : 's'}
              </Badge>
            </CardContent>
          </Card>

          {/* List */}
          {loading ? (
            <div className="text-sm text-gray-500 py-8 text-center flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading opportunities…
            </div>
          ) : opps.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500 text-sm">
                <Mail className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                No opportunities yet — click <b>+ New Opportunity</b> to capture the first one.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {opps.map((o) => (
                <Card
                  key={o.id}
                  className="hover:border-[#00B4D8]/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/odc/opportunities/${o.id}`)}
                  data-testid={`odc-row-${o.code}`}
                >
                  <CardContent className="py-3 flex items-center gap-4 flex-wrap">
                    <div className="font-mono text-xs text-gray-500 w-28">{o.code}</div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-sm font-semibold text-[#0F172A]">{o.title}</div>
                      <div className="text-xs text-gray-500 line-clamp-1">{o.summary}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(o.industry_tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 min-w-[100px]">
                      {o.country_target || '—'} · {o.currency} {o.estimated_value ? Number(o.estimated_value).toLocaleString() : '—'}
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-semibold ${STATUS_STYLES[o.status] || ''}`}>
                      {o.status}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <OdcTemplatesTab headers={headers} />
      )}

      {showForm && (
        <OdcOpportunityFormModal
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); reload(); }}
          headers={headers}
        />
      )}
    </div>
  );
};

export default OdcDashboard;
